import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip
} from '@material-ui/core';
import {
  Error as ErrorIcon,
  Info as InfoIcon,
  PersonSharp as Person,
} from '@material-ui/icons';
import { useTimelineViewStyles } from '../styles/timeline-view.styles';
import { statusIcons as statusIconsMap, statusColors as statusColorsMap, primitiveColors, skeletonConfig } from '../utils/constants';
import { StatusType } from '../constants/index';

/**
 * Props interface for the CustomAlert component
 */
interface CustomAlertProps {
  /** Severity level that determines color and icon */
  severity: 'error' | 'info' | 'success' | 'warning';
  /** Content to display in the alert */
  children: React.ReactNode;
  /** Optional action element to display on the right side */
  action?: React.ReactNode;
}

/**
 * CustomAlert Component
 * 
 * A custom alert component for Material-UI v4 compatibility that displays
 * messages with appropriate severity styling and icons. Provides consistent
 * alert styling across the application with color-coded backgrounds and borders.
 * 
 * @component
 * @param props - The component props
 * @param props.severity - Alert severity level affecting color and icon
 * @param props.children - Alert content to display
 * @param props.action - Optional action element for the right side
 * 
 * @returns A styled alert with severity-based coloring and optional action
 */
export const CustomAlert = ({ severity, children, action }: CustomAlertProps) => {
  const getColor = () => {
    switch (severity) {
      case 'error': return '#f44336';
      case 'info': return '#2196f3';
      case 'success': return '#4caf50';
      case 'warning': return '#ff9800';
      default: return '#2196f3';
    }
  };

  const getIcon = () => {
    switch (severity) {
      case 'error': return <ErrorIcon />;
      case 'info': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  return (
    <Paper 
      style={{ 
        padding: '12px 16px', 
        backgroundColor: `${getColor()}15`,
        border: `1px solid ${getColor()}30`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Box style={{ color: getColor(), display: 'flex' }}>
          {getIcon()}
        </Box>
        <Typography variant="body2" style={{ color: getColor() }}>
          {children}
        </Typography>
      </Box>
      {action && <Box>{action}</Box>}
    </Paper>
  );
};

/**
 * Props interface for the CustomSkeleton component
 */
interface CustomSkeletonProps {
  /** Visual variant of the skeleton */
  variant?: 'text' | 'rectangular';
  /** Width of the skeleton element */
  width?: number | string;
  /** Height of the skeleton element */
  height?: number | string;
  /** Additional CSS styles to apply */
  style?: React.CSSProperties;
}

/**
 * CustomSkeleton Component
 * 
 * A custom skeleton loading component for Material-UI v4 compatibility.
 * Provides animated placeholder elements during loading states with
 * configurable dimensions and styling variants.
 * 
 * @component
 * @param props - The component props
 * @param props.variant - Skeleton variant affecting border radius
 * @param props.width - Width of the skeleton element
 * @param props.height - Height of the skeleton element
 * @param props.style - Additional CSS styles
 * 
 * @returns An animated skeleton placeholder element
 */
export const CustomSkeleton = ({ 
  variant = 'text', 
  width = '100%', 
  height = 20,
  style = {} 
}: CustomSkeletonProps) => {
  const baseStyle: React.CSSProperties = {
    backgroundColor: '#f0f0f0',
    borderRadius: variant === 'text' ? '4px' : '0px',
    width,
    height,
    animation: 'shimmer 1.5s ease-in-out infinite',
    ...style
  };

  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0% { opacity: 1; }
            50% { opacity: 0.4; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <div style={baseStyle} />
    </>
  );
};

/**
 * LoadingSkeleton Component
 * 
 * Displays a skeleton loading state for the timeline view with placeholder
 * elements for time markers and milestones. Provides visual feedback during
 * data loading with animated skeleton elements positioned to match the
 * expected timeline layout.
 * 
 * @component
 * @returns A skeleton loading layout with time markers and milestone placeholders
 */
export const LoadingSkeleton = React.memo(() => {
  const classes = useTimelineViewStyles();
  
  return (
    <Box className={classes.content} data-testid="loading-skeleton">
      <Box className={classes.timeMarkersContainer}>
        <Box className={classes.timeMarkersContent}>
          {Array.from({ length: skeletonConfig.timeMarkers.count }).map((_, index) => (
            <CustomSkeleton 
              key={index} 
              variant="text"
              width={skeletonConfig.timeMarkers.dimensions.width}
              height={skeletonConfig.timeMarkers.dimensions.height} />
          ))}
        </Box>
      </Box>
      <Box className={classes.milestonesContainer} style={{ minHeight: 60, position: 'relative' }}>
        {Array.from({ length: skeletonConfig.milestones.count }).map((_, index) => (
          <Box key={index} style={{ position: 'absolute', left: `${skeletonConfig.milestones.spacing + index * skeletonConfig.milestones.spacing}%` }}>
            <CustomSkeleton variant="text" 
              width={skeletonConfig.milestones.textDimensions.width}
              height={skeletonConfig.milestones.textDimensions.height} />
            <CustomSkeleton variant="rectangular" 
              width={skeletonConfig.milestones.verticalLineDimensions.width}
              height={skeletonConfig.milestones.verticalLineDimensions.height}
              style={{ marginTop: skeletonConfig.milestones.verticalLineDimensions.marginTop }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
});

LoadingSkeleton.displayName = 'LoadingSkeleton';

/**
 * TimelineLegend Component
 * 
 * Displays a comprehensive legend for the timeline view showing primitive
 * colors and status indicators. Provides visual reference for understanding
 * the color coding and status symbols used throughout the timeline
 * visualization.
 * 
 * @component
 * @returns A legend with primitive colors and status indicators
 */
export const TimelineLegend = React.memo(() => {
  const classes = useTimelineViewStyles();
  
  return (
    <Box className={classes.legend}>
      <Typography variant="caption" className={classes.legendLabel}>
        Primitives:
      </Typography>
      {primitiveColors && Object.entries(primitiveColors).map(([name, color]) => ({name,color})).map((step) => (
        <Chip
          key={step.name}
          label={step.name}
          size="small"
          className={classes.legendChip}
          style={{ backgroundColor: step.color }}
        />
      ))}
      
      <Box className={classes.statusLegend}>
        <Typography variant="caption" className={classes.legendLabel}>
          Status:
        </Typography>
        {[
          { name: 'Completed', status: StatusType.COMPLETED },
          { name: 'Pending', status: StatusType.PENDING },
          { name: 'Error', status: StatusType.ERROR }
        ].map((item) => (
          <Box key={item.name} className={classes.statusItem}>
            <Box style={{ color: statusColorsMap[item.status as keyof typeof statusColorsMap] }}>
              {React.createElement(statusIconsMap[item.status as keyof typeof statusIconsMap], { 
                className: classes.statusIcon 
              })}
            </Box>
            <Typography variant="caption" className={classes.statusText}>
              {item.name}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box className={classes.statusItem}>
            <Box>
              {React.createElement(Person, { 
                className: classes.statusIcon 
              })}
            </Box>
            <Typography variant="caption" className={classes.statusText}>
              Manually Triggered Primitives
            </Typography>
          </Box>
    </Box>
  );
});

TimelineLegend.displayName = 'TimelineLegend';